import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ConfigProvider, theme } from 'antd';
import { loadDarkTheme, saveDarkTheme } from './themeStorage';

const ThemeContext = createContext(null);

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}

const baseAntdConfig = {
  getPopupContainer: (triggerNode) => {
    if (triggerNode?.closest?.('.ant-menu')) {
      return document.body;
    }
    return triggerNode?.parentNode || document.body;
  },
  modal: {
    autoFocusButton: null,
    focusTriggerAfterClose: false,
    keyboard: true,
  },
  select: {
    autoFocus: false,
  },
  input: {
    autoFocus: false,
  },
};

export function ThemeProvider({ children }) {
  const [darkMode, setDarkMode] = useState(() => loadDarkTheme());

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = darkMode ? 'dark' : 'light';
    root.style.colorScheme = darkMode ? 'dark' : 'light';
  }, [darkMode]);

  const toggleDarkMode = useCallback(() => {
    setDarkMode((prev) => {
      const next = !prev;
      saveDarkTheme(next);
      return next;
    });
  }, []);

  const themeConfig = useMemo(
    () => ({
      algorithm: darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
      token: darkMode
        ? {
            motion: false,
            colorPrimary: '#3880be',
            borderRadius: 6,
            colorBgLayout: '#0c1219',
            colorBgContainer: '#151c26',
            colorBgElevated: '#1a2330',
            colorBorder: '#2a3647',
            colorBorderSecondary: '#2a3647',
          }
        : {
            motion: false,
            colorPrimary: '#1890ff',
            borderRadius: 6,
          },
    }),
    [darkMode]
  );

  const contextValue = useMemo(
    () => ({ darkMode, toggleDarkMode }),
    [darkMode, toggleDarkMode]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <ConfigProvider {...baseAntdConfig} theme={themeConfig}>
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}
